const groupPrefix = createApiRoute(ApiModule.ORDER, ApiAuthorization.EMPLOYEE, ApiDataModel.CLIENT_DIETS)
const groupCartPrefix = createApiRoute(ApiModule.ORDER, ApiAuthorization.EMPLOYEE, ApiDataModel.CARTS)

export const apiOrders = {
	fetchOrders: async ({
		status,
		dietType,
		dietID,
		clientID,
		search,
		dateFrom,
		dateTo,
		page = 1,
		perPage = 10,
		filters,
	}: {
		status?: EOrderStatus[]
		dietType?: EOrderDietType[]
		dietID?: string[]
		clientID?: number
		search?: string
		dateFrom?: Date
		dateTo?: Date
		page?: number
		perPage?: number
		filters?: IApiFilters
	}) => {
		const formattedDateFrom = dateFrom ? dateToFilterFormat(dateFrom) : undefined
		const formattedDateTo = dateTo ? dateToFilterFormat(dateTo) : undefined

		const { data, meta } = await apiCommon<IOrder[]>(`${groupPrefix}`, {
			method: 'GET',
			...createQuery(
				{ page, perPage },
				{
					client_id: clientID,
					search,
					status,
					diet_type: dietType,
					diet_id: dietID,
					created_from: formattedDateFrom,
					created_to: formattedDateTo,
					...filters,
				},
			),
		})
		return { data: data.map(d => Order(d)), meta }
	},

	fetchOrder: async (orderID: string) => {
		const { data } = await apiCommon<IOrder>(`${groupPrefix}/${orderID}`, {
			method: 'GET',
		})
		return Order(data)
	},

	fetchOrderOperations: async (orderID: number) => {
		const { data } = await apiCommon<IOrderOperation[]>(`${groupPrefix}/${orderID}/operations`, {
			method: 'GET',
		})
		return { data: data.map(d => OrderOperation(d)) }
	},

	cancelOrders: async (ids: number[]) => {
		const { data } = await apiCommon<IOrder[]>(`${groupPrefix}/cancel`, {
			method: 'POST',
			body: { client_diet_ids: ids },
		})
		return data
	},

	fetchOrderAddresses: async (orderID: string) => {
		const { data } = await apiCommon<IClientAddress[]>(`${groupPrefix}/${orderID}/addresses`, { method: 'GET' })
		return data ? data.map(d => ClientAddress(d)) : []
	},

	calculateOrderDaysChangeDiet: async (orderID: number, dietID: number, kcal: number, days: string[]) => {
		const { data } = await apiCommon<IOrderDayChange>(`${groupPrefix}/${orderID}/change-diet/calculate`, {
			method: 'POST',
			body: {
				kcal,
				diet_id: dietID,
				client_diet_days: days,
			},
		})
		return OrderDayChange(data)
	},

	createNewCartFromClientDiet: async (payload: IOrderChangeDietRequest) => {
		const { data } = await apiCommon<IOrderChangeDietResponse>(
			`${groupPrefix}/${payload.clientDietID}/change-diet`,
			{
				method: 'POST',
				body: {
					diet_id: payload.newDietID,
					kcal: payload.newKcal,
					diet_config: {
						meal_slots: payload.newDietConfig,
					},
					client_diet_days: payload.newClientDietDays,
					all_available_days: payload.allAvailableDays,
				},
			},
		)
		return OrderChangeDiet(data)
	},

	confirmChangeDietCart: async (
		payload: IOrderConfirmChangeDietRequest,
	): Promise<IOrderConfirmChangeDietResponseModel> => {
		const { data } = await apiCommon<IOrderConfirmChangeDietResponse>(
			`${groupPrefix}/${payload.clientDietID}/change-diet/${payload.cartUUID}/confirm`,
			{
				method: 'POST',
				body: {
					price_to_pay: payload.priceToPay,
					price_to_refund: payload.priceToRefund,
					employee_payment_type: payload.employeePaymentType,
				},
			},
		)

		return OrderConfirmChangeDiet(data)
	},

	calculateOrderDaysChangeAddress: async (orderID: number, addressID: number, days: string[]) => {
		const { data } = await apiCommon<IOrderDayChange>(`${groupPrefix}/${orderID}/change-address/calculate`, {
			method: 'POST',
			body: {
				address_id: addressID,
				client_diet_days: days,
			},
		})
		return OrderDayChange(data)
	},

	updateOrderDayDiet: async (
		orderID: number,
		dietID: number,
		kcal: number,
		obj: IOrderDayChangeModel,
		paymentType: EEmployeePaymentType,
	) => {
		const { data } = await apiCommon<IOrderDay[]>(`${groupPrefix}/${orderID}/change-diet`, {
			method: 'POST',
			body: {
				diet_id: dietID,
				kcal,
				employee_payment_type: paymentType,
				...orderDayChangeToJSON(obj),
			},
		})
		return { data: data.map(OrderDay) }
	},

	updateOrderDayAddress: async (clientDietID: number, addressID: number, days: string[]) => {
		const { data } = await apiCommon<IOrderDay[]>(`${groupPrefix}/${clientDietID}/change-address`, {
			method: 'POST',
			body: { address_id: addressID, client_diet_days: days },
		})
		return { data: data.map(OrderDay) }
	},

	moveOrderDays: async (clientDietID: number, daysFrom: string[], daysTo: Date[]) => {
		const { data } = await apiCommon<IOrderDay[]>(`${groupPrefix}/${clientDietID}/move-days`, {
			method: 'POST',
			body: { dates: daysTo.map(dateToFilterFormat), client_diet_days: daysFrom },
		})
		return { data: data.map(OrderDay) }
	},
	cancelOrderDay: async (orderId: number, payload: IOrderClientDayCancelPayload): Promise<void> => {
		await apiCommon<IOrderDay[]>(`${groupPrefix}/${orderId}/cancel`, {
			method: 'POST',
			body: payload,
		})
	},
	calculateOrderDayAddon: async (orderID: number, addonID: number) => {
		const { data } = await apiCommon<IOrderDayChange>(`${groupPrefix}/${orderID}/addon/calculate`, {
			method: 'POST',
			body: {
				addons_id: [addonID],
			},
		})
		return OrderDayChange(data)
	},

	calculateOrderDayNewExtraMeal: async (
		clientID: number,
		parent_client_diet_uuid: string,
		days: { date: Date | string; addonIDs: number[] | null; mealSlots: IOrderDaysMealFormModel[] | null }[],
		promoCode?: string | null,
	): Promise<{ data: ICart }> => {
		const { data } = await apiCommon<ICart>(`${groupCartPrefix}/addons`, {
			method: 'POST',
			body: {
				client_id: clientID,
				parent_client_diet_uuid,
				...(promoCode !== undefined &&
					promoCode !== null &&
					promoCode.trim() !== '' && { code: promoCode.trim() }),
				diet_days: days.map(d => ({
					date: dateToFilterFormat(d.date),
					addons_slot: d.addonIDs,
					meal_slots: d.mealSlots ? d.mealSlots.map(orderDayMealToJSON) : null,
				})),
			},
		})
		return { data }
	},

	calculateClientDateCancel: async (clientDiet: number) => {
		const { data } = await apiCommon<IOrderRefultDetails>(`${groupPrefix}/${clientDiet}/cancel/calculate`, {
			method: 'POST',
		})
		return OrderRefundCalculate(data)
	},

	fetchMealSlotSizeConfigs: async (clientDietId: number) => {
		const { data } = await apiCommon<IMealSlotSizeConfig>(`${groupPrefix}/${clientDietId}/meal-slot-size-configs`, {
			method: 'GET',
		})
		return data
	},

	updateMealSlotsConfig: async (
		clientDietId: number,
		dates: string[],
		mealSlots: { meal_type: EMealType; size_id: number }[],
	) => {
		await apiCommon<string>(`${groupPrefix}/${clientDietId}/meal-slot-size-configs`, {
			method: 'POST',
			body: {
				dates,
				meal_slots: mealSlots,
			},
		})
	},

	clientDateCancel: async (clientDiet: number, refundAmount: number) => {
		const { data } = await apiCommon(`${groupPrefix}/${clientDiet}/cancel`, {
			method: 'POST',
			body: {
				refund_amount: refundAmount,
				employee_payment_type: EEmployeePaymentType.REFUND_POINTS,
			},
		})
		return data
	},
}
